import { JSX, useEffect, useMemo, useRef, useState } from 'react';
import {
  Controller,
  FieldErrors,
  FieldPath,
  UseFormReturn,
  useFieldArray,
} from 'react-hook-form';

import { Button } from 'components/Button';
import { Checkbox } from 'components/Checkbox';
import { ValueType } from 'components/DebounceSelect';
import { ReferenceNumberCard } from 'components/ReferenceNumberCard';
import { ReferenceNumberModal } from 'components/ReferenceNumberModal';
import { RHFLocationSelect } from 'components/input/RHFLocationSelect';
import { InputValue } from 'components/input/RHFTextInput';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { LoadDateTimeInput } from 'pages/LoadView/LoadInformation/Components';
import {
  getMcleodIntegrationFromList,
  getReferenceNumberOptions,
} from 'pages/QuoteView/LoadBuilding/McleodSectionForms/helpers';
import { mcleodRouteStopLabel } from 'pages/QuoteView/LoadBuilding/McleodSectionForms/mcleodRouteStops';
import {
  applySelectedLocationToMcleodStop,
  getMcleodStopLocationView,
  isNestedMcleodRouteStopKey,
  mcleodAdditionalReferencesPath,
  mcleodStopFieldPath,
} from 'pages/QuoteView/LoadBuilding/McleodSectionForms/stopFieldPaths';
import {
  AdditionalReference,
  LoadStopTypes,
  NormalizedLoad,
  TMSLocation,
} from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';

import { LoadBuildingTextInput } from '../components/LoadBuildingTextInput';

export function StopForm({
  stopKey,
  formMethods,
  isLoadingLocations,
  locations,
  handleRefreshLocations,
  setLocations,
  aiHighlightedExternalTMSID,
  showPoField = false,
  showComments = false,
}: {
  stopKey: LoadStopTypes | string;
  formMethods: UseFormReturn<NormalizedLoad>;
  isLoadingLocations: boolean;
  locations: Maybe<TMSLocation[]>;
  handleRefreshLocations: () => void;
  setLocations: React.Dispatch<React.SetStateAction<Maybe<TMSLocation[]>>>;
  /** If set, the matching location option is marked as AI-suggested in the dropdown (same as 3G). */
  aiHighlightedExternalTMSID?: string;
  showPoField?: boolean;
  /** Hot comment on every stop; billing comment on the pickup (first stop) only. */
  showComments?: boolean;
}): JSX.Element {
  const [isEditingLocation] = useState(false);
  const [isRefNumberModalOpen, setIsRefNumberModalOpen] = useState(false);
  const [editingRefIndex, setEditingRefIndex] = useState<Maybe<number>>(null);
  const [tempRefNumber, setTempRefNumber] = useState<AdditionalReference>({
    qualifier: '',
    number: '',
    weight: 0,
    pieces: 0,
    shouldSendToDriver: false,
  });
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { tmsIntegrations } = useServiceFeatures();
  const tms = getMcleodIntegrationFromList(tmsIntegrations);
  const {
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = formMethods;

  const additionalReferencesPath = mcleodAdditionalReferencesPath(stopKey);

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: additionalReferencesPath,
  });

  const qualifierCodes = getReferenceNumberOptions(tms);
  const defaultApptTimezone =
    tms?.tenant?.includes('tmka') === true ? 'America/Chicago' : 'UTC';

  const getFieldValidation = (fieldName: string) => {
    if (
      tms?.featureFlags.isOnlyCityStateRequired === true &&
      !['city', 'state'].includes(fieldName)
    ) {
      return {};
    }

    if (!watchedLocationID) {
      return { required: 'Required if existing ID not selected' };
    }
    if (isEditingLocation) {
      return { required: 'Required' };
    }
    return {};
  };

  const watchedLocationID = watch(
    mcleodStopFieldPath(stopKey, 'externalTMSID')
  );
  const watchedStopRaw = watch(stopKey as FieldPath<NormalizedLoad>);
  const watchedLocObj = getMcleodStopLocationView(stopKey, watchedStopRaw);
  const watchedApptRequired = Boolean(
    (watchedStopRaw as { apptRequired?: boolean })?.apptRequired
  );
  const watchedLoad = getValues();

  const handleRefNumberModalOk = () => {
    if (!tempRefNumber.qualifier?.trim() || !tempRefNumber.number?.trim()) {
      return;
    }

    const newRef: AdditionalReference = { ...tempRefNumber };

    if (editingRefIndex !== null) {
      update(editingRefIndex, newRef);
    } else {
      append(newRef);
    }

    setTempRefNumber({
      qualifier: '',
      number: '',
      weight: 0,
      pieces: 0,
      shouldSendToDriver: false,
    });

    setIsRefNumberModalOpen(false);
    setEditingRefIndex(null);
  };

  const handleRefNumberModalCancel = () => {
    setTempRefNumber({
      qualifier: '',
      number: '',
      weight: 0,
      pieces: 0,
      shouldSendToDriver: false,
    });

    setIsRefNumberModalOpen(false);
    setEditingRefIndex(null);
  };

  const handleEditReference = (index: number) => {
    const reference = fields[index] as AdditionalReference;
    setTempRefNumber({
      qualifier: reference.qualifier,
      number: reference.number,
      weight: reference.weight,
      pieces: reference.pieces,
      shouldSendToDriver: reference.shouldSendToDriver,
    });
    setEditingRefIndex(index);
    setIsRefNumberModalOpen(true);
  };

  const handleAddNewReference = () => {
    setTempRefNumber({
      qualifier: '',
      number: '',
      weight: 0,
      pieces: 0,
      shouldSendToDriver: false,
    });
    setEditingRefIndex(null);
    setIsRefNumberModalOpen(true);
  };

  const handleTempRefChange = (
    field: keyof AdditionalReference,
    value: unknown
  ) => {
    setTempRefNumber((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    if (watchedLocationID) {
      const selectedLoc = locations?.find(
        (loc) => loc.externalTMSID === watchedLocationID
      );
      if (!selectedLoc) {
        return;
      }

      applySelectedLocationToMcleodStop(
        stopKey,
        selectedLoc as Record<string, unknown>,
        setValue
      );
    }
    // Keyed on watchedLocationID; locations/setValue churn on search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedLocationID]);

  useEffect(() => {
    if (watchedLocationID) {
      const selectedLoc = locations?.find(
        (loc) => loc.externalTMSID === watchedLocationID
      );
      if (!selectedLoc) {
        return;
      }

      // Compare against the stop's current values, not this render's watched snapshot. Picking a
      // location changes the ID and, in the effect above, writes the new address in the same
      // commit, so the snapshot still holds the previous address and would read as a mismatch,
      // clearing the ID that was just picked.
      const currentLocObj = getMcleodStopLocationView(
        stopKey,
        getValues(stopKey as FieldPath<NormalizedLoad>)
      );

      if (
        (selectedLoc?.name &&
          currentLocObj?.name?.toLowerCase() !==
            selectedLoc?.name?.toLowerCase()) ||
        (selectedLoc?.addressLine1 &&
          currentLocObj?.addressLine1?.toLowerCase() !==
            selectedLoc?.addressLine1?.toLowerCase()) ||
        (selectedLoc?.addressLine2 &&
          currentLocObj?.addressLine2?.toLowerCase() !==
            selectedLoc?.addressLine2?.toLowerCase()) ||
        (selectedLoc?.city &&
          currentLocObj?.city?.toLowerCase() !==
            selectedLoc?.city?.toLowerCase()) ||
        (selectedLoc?.state &&
          currentLocObj?.state?.toLowerCase() !==
            selectedLoc?.state?.toLowerCase()) ||
        (selectedLoc?.zipCode &&
          currentLocObj?.zipCode?.toLowerCase() !==
            selectedLoc?.zipCode?.toLowerCase())
      ) {
        setValue(mcleodStopFieldPath(stopKey, 'externalTMSID'), '', {
          shouldDirty: true,
        });
      }
    }
    // locations/setValue omitted to avoid clearing ID while searching.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedLocationID,
    watchedLocObj?.name,
    watchedLocObj?.addressLine1,
    watchedLocObj?.addressLine2,
    watchedLocObj?.city,
    watchedLocObj?.state,
    watchedLocObj?.zipCode,
  ]);

  useEffect(() => {
    if (fields.length === 1 && scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  }, [fields.length]);

  const modalStopKey = useMemo(() => {
    if (
      stopKey === LoadStopTypes.Pickup ||
      stopKey === LoadStopTypes.Consignee
    ) {
      return stopKey;
    }

    if (isNestedMcleodRouteStopKey(stopKey)) {
      const match = stopKey.match(/^stops\.(\d+)$/);
      if (match) {
        const index = Number(match[1]);
        const routeStops = getValues('stops') ?? [];
        return mcleodRouteStopLabel(routeStops, index);
      }
    }

    return LoadStopTypes.Consignee;
  }, [getValues, stopKey]);

  return (
    <>
      <RHFLocationSelect
        required={false}
        disabled={isEditingLocation}
        name={mcleodStopFieldPath(stopKey, 'externalTMSID')}
        label='Location'
        control={control}
        placeholder='Search by name, address, or city'
        errors={errors}
        locations={locations}
        setLocations={setLocations}
        isLoadingLocations={isLoadingLocations}
        handleRefreshLocations={handleRefreshLocations}
        aiHighlightedExternalTMSID={aiHighlightedExternalTMSID}
        valueRenderer={(selectedOption: ValueType) => {
          return {
            label: selectedOption.name || selectedOption.label,
            value: selectedOption.value,
          };
        }}
      />

      {isEditingLocation && <hr className='my-2' />}

      <LoadBuildingTextInput
        name={mcleodStopFieldPath(stopKey, 'name')}
        label='Name'
        options={getFieldValidation('name')}
      />

      <LoadBuildingTextInput
        name={mcleodStopFieldPath(stopKey, 'addressLine1')}
        label='Address Line 1'
        options={getFieldValidation('addressLine1')}
      />

      <Grid cols='2' gap='sm' className='w-full m-0'>
        <LoadBuildingTextInput
          name={mcleodStopFieldPath(stopKey, 'addressLine2')}
          label='Address Line 2'
        />

        <LoadBuildingTextInput
          name={mcleodStopFieldPath(stopKey, 'city')}
          label='City'
          options={getFieldValidation('city')}
        />
      </Grid>
      <Grid cols='2' gap='sm' className='w-full m-0'>
        <LoadBuildingTextInput
          name={mcleodStopFieldPath(stopKey, 'state')}
          label='State'
          options={getFieldValidation('state')}
        />

        <LoadBuildingTextInput
          name={mcleodStopFieldPath(stopKey, 'zipCode')}
          label='Zip Code'
          placeholder='12345'
          options={getFieldValidation('zipCode')}
        />
      </Grid>

      {showPoField && (
        <LoadBuildingTextInput
          name={mcleodStopFieldPath(stopKey, 'refNumber')}
          label='PO #'
          placeholder='Purchase order number'
        />
      )}

      <Typography
        variant='h6'
        weight='medium'
        className='text-neutral-800 mt-3 mb-2'
      >
        Contact Information
      </Typography>

      <Grid cols='2' gap='sm' className='w-full m-0'>
        <LoadBuildingTextInput
          name={mcleodStopFieldPath(stopKey, 'contact')}
          label='Contact Person'
          required={false}
          placeholder='Contact name'
        />

        <LoadBuildingTextInput
          name={mcleodStopFieldPath(stopKey, 'phone')}
          label='Phone'
          required={false}
          placeholder='(555) 123-4567'
          inputValue={InputValue.PHONE_NUMBER}
        />
      </Grid>

      <Flex direction='col' gap='sm' className='mt-1'>
        <Flex align='center' justify='between' className='mr-px'>
          <Typography variant='h6' weight='medium' className='text-neutral-800'>
            Reference Numbers
          </Typography>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={handleAddNewReference}
            className='h-8 px-3 text-xs text-brand-500 hover:-mx-px'
            buttonNamePosthog={
              ButtonNamePosthog.LoadBuildingAddMcleodReferenceNumber
            }
          >
            + Add Reference
          </Button>
        </Flex>

        {fields.length > 0 && (
          <div className='relative'>
            <Flex
              align='start'
              gap='md'
              ref={scrollContainerRef}
              className={`overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-neutral-300 scrollbar-track-neutral-100 ${
                fields.length === 1 ? 'justify-center' : ''
              }`}
            >
              {fields.map((field, index) => {
                const ref = field as AdditionalReference;

                return (
                  <ReferenceNumberCard
                    key={field.id}
                    reference={ref}
                    index={index}
                    onRemove={remove}
                    onEdit={handleEditReference}
                  />
                );
              })}
            </Flex>
            {fields.length > 1 && (
              <Typography
                variant='body-sm'
                align='center'
                className='text-neutral-500 mt-1'
              >
                ← Scroll to view more references →
              </Typography>
            )}
          </div>
        )}

        <ReferenceNumberModal
          qualifierOptions={qualifierCodes}
          isOpen={isRefNumberModalOpen}
          onCancel={handleRefNumberModalCancel}
          onOk={handleRefNumberModalOk}
          editingRefIndex={editingRefIndex}
          tempRefNumber={tempRefNumber}
          onTempRefChange={handleTempRefChange}
          stop={modalStopKey}
        />
      </Flex>

      {showComments && (
        <>
          <Typography
            variant='h6'
            weight='medium'
            className='text-neutral-800 mt-3 mb-2'
          >
            Comments
          </Typography>

          <LoadBuildingTextInput
            name={mcleodStopFieldPath(stopKey, 'hotComment')}
            label='Hot Comment'
            placeholder='Prints on the carrier rate con'
          />

          {stopKey === 'stops.0' && (
            <LoadBuildingTextInput
              name={mcleodStopFieldPath(stopKey, 'billingComment')}
              label='Billing Comment'
              placeholder='Internal note for accounting'
            />
          )}
        </>
      )}

      {(watchedApptRequired || isEditingLocation || !watchedLocationID) && (
        <Controller
          name={mcleodStopFieldPath(stopKey, 'apptRequired')}
          control={control}
          render={({ field }) => (
            <Checkbox
              label='Appointment Required?'
              labelClassName='leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-neutral-500'
              checked={field.value === true}
              onCheckedChange={(checked) => field.onChange(checked)}
            />
          )}
        />
      )}

      {isEditingLocation && <hr className='my-2' />}

      <LoadDateTimeInput
        name={mcleodStopFieldPath(stopKey, 'apptStartTime')}
        label='Appointment Start Time'
        load={watchedLoad}
        defaultTimezone={defaultApptTimezone}
        disablePastDates={false}
        required
      />

      <LoadDateTimeInput
        name={mcleodStopFieldPath(stopKey, 'apptEndTime')}
        label='Appointment End Time'
        load={watchedLoad}
        defaultTimezone={defaultApptTimezone}
        disablePastDates={false}
      />
    </>
  );
}

export function hasNoErrorsInSubsection(
  subsectionPath: string,
  formErrors: FieldErrors
): boolean {
  const parts = subsectionPath.split('.');
  let current: unknown = formErrors;

  for (const part of parts) {
    if (!current || typeof current !== 'object') {
      return true;
    }

    current = (current as Record<string, unknown>)[part];
  }

  if (current == null) {
    return true;
  }

  if (typeof current !== 'object') {
    return false;
  }

  const errorNode = current as Record<string, unknown> & { message?: unknown };
  if (errorNode.message != null) {
    return false;
  }

  return Object.keys(errorNode).length === 0;
}
